import { ILocaleProvider } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";
import { action, computed, makeObservable, observable, reaction } from "mobx";
import { ChangeEvent, FunctionComponent, SyntheticEvent } from "react";
import { ImageSize } from "src/c8-platform/type/image";
import { TableHeight } from "src/c8-platform/type/table";
import CustomViewDialogStore from "../CustomViewDialogStore";
import { ICustomViewTableDropDownOptions, ICustomViewTableOptions, IMAGE_DIMENSION_MIN } from "./CustomViewOptions";
import { CustomViewQueryOptionsStore } from "./CustomViewQueryOptionsStore";

/**
 * This store contains information about table view
 */
class TableCustomViewOptionsStore<T extends ICustomViewTableOptions = ICustomViewTableOptions> extends CustomViewQueryOptionsStore<T> {
	/** the image height validation error indication */
	@observable hasImageHeightValidationError: boolean = false;

	/** the image height displaying value (show initial and user modified value) */
	@observable imageHeight: string = "";

	/** validation error message for image height */
	@observable imageHeightValidationMsg: string = "";

	/** all the dropdown boxes selection options for the custom view */
	readonly tableDropDownOptions: ICustomViewTableDropDownOptions;

	/** locale provider */
	readonly loc: ILocaleProvider;

	constructor(
		tableDropDownOptions: ICustomViewTableDropDownOptions,
		component: FunctionComponent,
		customViewDialogStore: CustomViewDialogStore,
		options?: T,
	) {
		super(tableDropDownOptions, component, customViewDialogStore, options);
		makeObservable(this);

		this.loc = customViewDialogStore.loc;
		this.imageHeightValidationMsg = this.imageHeightRangeMsg;
		this.tableDropDownOptions = tableDropDownOptions;
		this.imageHeight = this.imageHeightTextDefaultValue;

		this.trackReaction(reaction(
			() => this.tableOptions.tableImageHeight,
			() => {
				this.imageHeight = this.imageHeightTextDefaultValue;
				this.imageHeightValidationMsg = this.imageHeightRangeMsg;
				this.hasImageHeightValidationError = false;
			},
		));
	}

	/** returns all the table display options */
	get tableOptions(): T {
		return this.options;
	}

	/** default value of the image height field */
	get imageHeightTextDefaultValue(): string {
		const { tableImageHeight } = this.tableOptions;
		return tableImageHeight ? tableImageHeight.toString() : IMAGE_DIMENSION_MIN.toString();
	};

	/** image height valid range message */
	get imageHeightRangeMsg(): string {
		return this.loc.resource("ViewPreferences_MinimumValueError", [32]);
	}	

	/** set the value for chartHeight */
	@action onChangeChartHeight = (event: ChangeEvent<{}>, value: ComponentItem): void => {
		this.tableOptions.chartHeight = value.id;
	};

	/** set the value for chartHideTable */
	@action setChartHideTable = (event: SyntheticEvent, value: boolean): void => {
		this.tableOptions.chartHideTable = value;
	};

	/** set the value for printDynamicColumns */
	@action setPrintDynamicColumns = (event: SyntheticEvent, value: boolean): void => {
		this.tableOptions.printDynamicColumns = value;
	};

	/** set the value for printFrozenColumnsForEachPage */
	@action setPrintFrozenColumnsForEachPage = (event: SyntheticEvent, value: boolean): void => {
		this.tableOptions.printFrozenColumnsForEachPage = value;
	};

	/** set the value for tableHideRoot */
	@action setTableHideRoot = (event: SyntheticEvent, value: boolean): void => {
		this.tableOptions.tableHideRoot = value;
	};

	/** set the value for tableAutomaticHeight */
	@action setTableAutomaticHeight = (event: SyntheticEvent, value: boolean): void => {
		this.tableOptions.tableAutomaticHeight = value;
	};

	/** set the value for tableHeight */
	@action setTableHeight = (event: ChangeEvent<{}>, value: ComponentItem): void => {
		this.tableOptions.tableHeight = value.id as TableHeight;
	};

	/** set the value for query size */
	@action setQuerySize = (event: ChangeEvent<{}>, item: ComponentItem): void => {
		this.tableOptions.querySize = item.value;
	};

	/** set the value for expandLevel */
	@action setExpandLevel = (event: ChangeEvent<{}>, item: ComponentItem): void => {
		this.tableOptions.expandLevel = item.value;
	};

	/** set the value for tableFreezeColumns */
	@action setTableFreezeColumns = (event: ChangeEvent<{}>, item: ComponentItem): void => {
		this.tableOptions.tableFreezeColumns = item.value;
	};

	/** set the value for imageHeight */
	@action setImageHeight = (event: React.ChangeEvent<HTMLInputElement>): void => {
		const imgHeight= event.target.value;
		const value = Number(imgHeight);
		
		if (isNaN(value) || value < 0) {
			this.imageHeightValidationMsg = this.loc.resource("ValidateNotValid");
			this.imageHeight = imgHeight;
			this.hasImageHeightValidationError = true;
		}
		else if (value < 32) {
			this.imageHeightValidationMsg = this.imageHeightRangeMsg;
			this.imageHeight = imgHeight;
			this.hasImageHeightValidationError = true;
		} else {
			this.imageHeightValidationMsg = this.imageHeightRangeMsg;
			this.tableOptions.tableImageHeight = value;
			this.imageHeight = imgHeight;
			this.hasImageHeightValidationError = false;
		}
	};

	/** set the value for imageSize */
	@action setImageSize = (event: ChangeEvent<{}>, value: ComponentItem): void => {
		this.tableOptions.imageSize = value.id as ImageSize;
	};

	@computed get selectedTableHeight(): ComponentItem {
		const { tableHeight } = this.options;
		return this.tableDropDownOptions.tableHeights.find((item) => item.id === tableHeight);
	};

	@computed get selectedTableFreezeColumns(): ComponentItem {
		const { tableFreezeColumns } = this.options;
		return this.tableDropDownOptions.freezeColumns.find((item) => item.value === tableFreezeColumns);
	};

	@computed get selectedQuerySize(): ComponentItem {
		const { querySize } = this.options;
		return this.tableDropDownOptions.resultCounts.find((item) => item.value === querySize);
	};

	@computed get selectedImageSizes(): ComponentItem {
		const { imageSize } = this.options;
		return this.tableDropDownOptions.imageSizes.find((item) => item.id === imageSize);
	};

	@computed get selectedExpandLevel(): ComponentItem {
		const { expandLevel } = this.options;
		return this.tableDropDownOptions.expandLevels.find((item) => item.value === expandLevel);
	};

	@computed get selectedChartHeight(): ComponentItem {
		const { chartHeight } = this.options;
		return this.tableDropDownOptions.chartHeights.find((item) => item.id === chartHeight);
	};
}

export = TableCustomViewOptionsStore;